import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';

const BASE = `${LEAD_API_PREFIX}/breeze`;
const BREEZE_KPI = `${LEAD_API_PREFIX}/breeze/kpi`;

const BREEZE_CREDS = { withCredentials: true as const };

/** Persists ICICI redirect handover token for this browser tab (sessionStorage). Sent on every Breeze → lead-service call. */
export const BREEZE_APISSESSION_SESSION_KEY = 'breeze_icici_apisession';

export function persistApisessionHandover(token: string): void {
  const t = token.trim();
  if (!t || typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(BREEZE_APISSESSION_SESSION_KEY, t);
}

export function getStoredApisession(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  const v = sessionStorage.getItem(BREEZE_APISSESSION_SESSION_KEY);
  return v?.trim() ? v.trim() : null;
}

export function clearStoredApisession(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(BREEZE_APISSESSION_SESSION_KEY);
}

function apisessionHeaders(): Record<string, string> {
  const v = getStoredApisession();
  if (!v) return {};
  return { 'X-Breeze-Apisession': v };
}

export interface BreezeStatus {
  configured: boolean;
  loginUrl: string | null;
  hasSession: boolean;
  exchangedAt: string | null;
  docsUrl?: string;
  /** True when servlet session marks Breeze as linked (cookie + server flag). */
  cookieSessionLinked?: boolean;
}

/** GET /api/v1/breeze/status */
export async function getBreezeStatus() {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/status`,
    headers: apisessionHeaders(),
    ...BREEZE_CREDS,
  });
}

/** POST body { apiSession } */
export async function exchangeBreezeSession(apiSession: string) {
  return HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/session`,
    data: { apiSession },
    headers: apisessionHeaders(),
    ...BREEZE_CREDS,
  });
}

export async function clearBreezeSession() {
  return HttpClient.DELETE({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/session`,
    headers: apisessionHeaders(),
    ...BREEZE_CREDS,
  });
}

export async function syncBreezePortfolio() {
  return HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/portfolio/sync`,
    data: {},
    headers: apisessionHeaders(),
    ...BREEZE_CREDS,
  });
}

export async function getBreezePortfolioLatest() {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/portfolio/latest`,
    headers: apisessionHeaders(),
    ...BREEZE_CREDS,
  });
}

export async function getBreezeCollateralAnalysis() {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/analysis/collateral`,
    headers: apisessionHeaders(),
    ...BREEZE_CREDS,
  });
}

/** Live NSE+BSE portfolio holdings: avg, CMP, P/L per row (GET /insights/holdings-pl). */
export async function getBreezeHoldingsPlInsights() {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/insights/holdings-pl`,
    headers: apisessionHeaders(),
    ...BREEZE_CREDS,
  });
}

export interface BreezeHistoricalChartParams {
  stockCode: string;
  exchangeCode?: string;
  interval?: string;
  from?: string;
  to?: string;
}

/** GET /charts/historical — Breeze GetHistoricalCharts (v1) + simple pattern hints. */
export async function getBreezeHistoricalChart(params: BreezeHistoricalChartParams) {
  const q: Record<string, string> = { stockCode: params.stockCode };
  if (params.exchangeCode) q.exchangeCode = params.exchangeCode;
  if (params.interval) q.interval = params.interval;
  if (params.from) q.from = params.from;
  if (params.to) q.to = params.to;
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/charts/historical`,
    params: q,
    headers: apisessionHeaders(),
    ...BREEZE_CREDS,
  });
}

export async function getBreezeOpportunities() {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/analysis/opportunities`,
    headers: apisessionHeaders(),
    ...BREEZE_CREDS,
  });
}

/** GET /api/v1/breeze/dematholdings — live ICICI demat (signed GET, body "{}" per docs). */
export async function getBreezeDematHoldings() {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/dematholdings`,
    headers: apisessionHeaders(),
    ...BREEZE_CREDS,
  });
}

/** UI-computed portfolio KPI persisted server-side (no ICICI call). */
export interface BreezeKpiSnapshotPayload {
  capturedAt?: string;
  stockCount: number;
  largeCapPct: number;
  midCapPct: number;
  smallCapPct: number;
  unclassifiedCapPct?: number;
  investedAmount: number;
  currentValue: number;
  profitLoss: number;
  profitLossPct?: number | null;
}

export async function postBreezeKpiSnapshot(data: BreezeKpiSnapshotPayload) {
  return HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BREEZE_KPI}/snapshots`,
    data,
    headers: apisessionHeaders(),
    ...BREEZE_CREDS,
  });
}

export async function getBreezeKpiSnapshots(fromIso: string, toIso: string) {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BREEZE_KPI}/snapshots`,
    params: { from: fromIso, to: toIso },
    headers: apisessionHeaders(),
    ...BREEZE_CREDS,
  });
}

/**
 * ICICI Breeze redirect commonly uses lowercase `apisession`, e.g.
 * `https://whatzneu.com/?apisession=55343286` (see api.icicidirect.com apiuser login flow).
 */
export const BREEZE_API_SESSION_PARAM_KEYS = [
  'apisession',
  'API_Session',
  'Api_Session',
  'session',
] as const;

/** Read OAuth session token from URL query (or fragment) search params. */
export function pickApiSessionFromSearchParams(params: URLSearchParams): string {
  for (const k of BREEZE_API_SESSION_PARAM_KEYS) {
    const v = params.get(k);
    if (v?.trim()) return v.trim();
  }
  return '';
}

/** Try to read API_Session / apisession from pasted redirect URL or raw token. */
export function parseApiSession(input: string): string {
  const t = input.trim();
  if (!t) return '';
  if (!t.includes('http') && !t.includes('=')) {
    return t;
  }
  try {
    const u = new URL(t.includes('://') ? t : `https://dummy.local/${t}`);
    const q = u.searchParams;
    const fromQs = pickApiSessionFromSearchParams(q);
    if (fromQs) return decodeURIComponent(fromQs);
  } catch {
    /* ignore */
  }
  const m = t.match(/(?:API_Session|apisession)=([^&]+)/i);
  if (m) return decodeURIComponent(m[1]);
  return t;
}
